import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Check, X, Sparkles, Building2, Users, Zap, ArrowLeft, ArrowRight, CreditCard, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_FEATURES = {
  free: {
    icon: Zap,
    color: 'bg-gray-100 dark:bg-gray-800',
    badge: null,
    popular: false,
  },
  starter: {
    icon: Sparkles,
    color: 'bg-blue-50 dark:bg-blue-950',
    badge: null,
    popular: false,
  },
  pro: {
    icon: Users,
    color: 'bg-amber-50 dark:bg-amber-950',
    badge: 'Most Popular',
    popular: true,
  },
  ministry: {
    icon: Building2,
    color: 'bg-purple-50 dark:bg-purple-950',
    badge: 'Best Value',
    popular: false,
  },
};

const FEATURE_LABELS = {
  max_projects: 'Projects',
  ai_generations_per_month: 'AI Generations/month',
  max_slides_per_project: 'Slides per project',
  storage_mb: 'Storage',
  can_create_community: 'Create communities',
  max_communities: 'Communities',
  max_community_members: 'Members per community',
  export_pdf: 'PDF Export',
  export_pptx: 'PowerPoint Export',
  watermark: 'Watermark-free',
  live_streaming: 'Live Streaming (OBS)',
  service_calendar: 'Service Calendar',
  appointments: 'Appointments',
  custom_branding: 'Custom Branding',
  api_access: 'API Access',
  priority_support: 'Priority Support',
};

const formatValue = (key, value) => {
  if (value === -1) return 'Unlimited';
  if (value === true) return <Check className="w-5 h-5 text-green-500" />;
  if (value === false) return <X className="w-5 h-5 text-gray-300" />;
  if (key === 'storage_mb') {
    if (value >= 1024) return `${(value / 1024).toFixed(0)} GB`;
    return `${value} MB`;
  }
  if (key === 'watermark') return value ? <X className="w-5 h-5 text-gray-300" /> : <Check className="w-5 h-5 text-green-500" />;
  return value;
};

export default function PricingPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [creditPackages, setCreditPackages] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [creditBalance, setCreditBalance] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [purchasingCredits, setPurchasingCredits] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    fetchTiers();
    fetchCreditPackages();
    if (token) {
      fetchCurrentSubscription();
      fetchCreditBalance();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Handle URL-param toasts that DO NOT need auth (runs once on mount).
  // We defer via requestAnimationFrame so the Sonner <Toaster/> at the App
  // root is guaranteed to be mounted and listening before we fire toast().
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('canceled') !== '1' && params.get('paypal') !== 'canceled') return;
    const cleanUrl = () => window.history.replaceState({}, '', '/pricing');
    const fire = () => {
      if (params.get('canceled') === '1') toast.info('Checkout canceled.');
      if (params.get('paypal') === 'canceled') toast.info('PayPal checkout canceled.');
      cleanUrl();
    };
    // Two rAFs = "next paint after this paint" — enough for Toaster to hydrate.
    requestAnimationFrame(() => requestAnimationFrame(fire));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle redirects back from Stripe/PayPal (token-gated)
  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(window.location.search);
    const cleanUrl = () => window.history.replaceState({}, '', '/pricing');

    // Stripe success — poll status
    const sessionId = params.get('session_id');
    if (params.get('success') === '1' && sessionId) {
      (async () => {
        let paid = false;
        for (let i = 0; i < 6 && !paid; i++) {   // up to ~12s
          const r = await fetch(`${API_URL}/api/payments/stripe/status/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await r.json();
          if (data.payment_status === 'paid') { paid = true; break; }
          if (data.status === 'expired') break;
          await new Promise((res) => setTimeout(res, 2000));
        }
        if (paid) {
          toast.success('Payment confirmed! Your new tier is active.');
          fetchCurrentSubscription();
        } else {
          toast.info('Waiting on Stripe confirmation. Refresh in a moment if your tier hasn\u2019t updated.');
        }
        cleanUrl();
      })();
    }

    // PayPal success — capture the order
    if (params.get('paypal') === 'success') {
      const orderId = params.get('token') || sessionStorage.getItem('pending_paypal_order');
      if (orderId) {
        (async () => {
          const r = await fetch(`${API_URL}/api/payments/paypal/capture`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ order_id: orderId }),
          });
          const data = await r.json();
          if (r.ok && data.paid) {
            toast.success('PayPal payment confirmed! Your new tier is active.');
            fetchCurrentSubscription();
          } else {
            toast.error('PayPal capture failed. Please contact support if you were charged.');
          }
          sessionStorage.removeItem('pending_paypal_order');
          cleanUrl();
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const r = await fetch(`${API_URL}/api/payments/subscription/cancel`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (r.ok) { toast.success(data.message || 'Subscription set to cancel'); fetchCurrentSubscription(); }
      else toast.error(data.detail || 'Cancel failed');
    } catch { toast.error('Cancel failed'); }
    finally {
      setCanceling(false);
      setCancelDialogOpen(false);
    }
  };

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/tiers`);
      const data = await res.json();
      setTiers(data.tiers || []);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    }
  };

  const fetchCreditPackages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/credit-packages`);
      const data = await res.json();
      setCreditPackages(data.packages || []);
    } catch (error) {
      console.error('Failed to fetch credit packages:', error);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/current`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentSubscription(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditBalance = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/credit-balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCreditBalance(data);
      }
    } catch (error) {
      console.error('Failed to fetch credit balance:', error);
    }
  };

  const handlePurchaseCredits = async (packageId) => {
    if (!token) {
      navigate('/login?redirect=/pricing');
      return;
    }

    setPurchasingCredits(packageId);
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/purchase-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ package_id: packageId }),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Credits purchased successfully!');
        fetchCreditBalance();
      } else {
        toast.error(data.detail || 'Failed to purchase credits');
      }
    } catch (error) {
      toast.error('Failed to purchase credits');
    } finally {
      setPurchasingCredits(null);
    }
  };

  const [payTier, setPayTier] = useState(null); // shows the "Stripe or PayPal?" dialog

  const handleUpgrade = async (tier) => {
    if (!token) {
      navigate('/login?redirect=/pricing');
      return;
    }
    if (currentSubscription?.subscription?.tier === tier) {
      toast.info('You are already on this plan');
      return;
    }
    if (tier === 'free') {
      // Downgrade path: keep the legacy /api/subscriptions/upgrade endpoint for free
      setUpgrading(tier);
      try {
        const res = await fetch(`${API_URL}/api/subscriptions/upgrade`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ tier, billing_cycle: 'monthly' }),
        });
        const data = await res.json();
        if (res.ok) { toast.success(data.message || 'Subscription updated!'); fetchCurrentSubscription(); }
        else toast.error(data.detail || 'Failed to update');
      } finally { setUpgrading(null); }
      return;
    }
    // Paid tiers → open the provider chooser
    setPayTier(tier);
  };

  const payWithStripe = async () => {
    if (!payTier) return;
    setUpgrading(payTier);
    try {
      const res = await fetch(`${API_URL}/api/payments/stripe/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ package_id: payTier, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;   // redirect to Stripe Checkout
      } else {
        toast.error(data.detail || 'Stripe checkout failed');
        setUpgrading(null);
      }
    } catch {
      toast.error('Stripe checkout failed');
      setUpgrading(null);
    }
  };

  const payWithPayPal = async () => {
    if (!payTier) return;
    setUpgrading(payTier);
    try {
      const res = await fetch(`${API_URL}/api/payments/paypal/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ package_id: payTier, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (res.ok && data.approve_url) {
        sessionStorage.setItem('pending_paypal_order', data.order_id);
        window.location.href = data.approve_url;
      } else {
        toast.error(data.detail || 'PayPal checkout failed');
        setUpgrading(null);
      }
    } catch {
      toast.error('PayPal checkout failed');
      setUpgrading(null);
    }
  };

  const getButtonText = (tier) => {
    if (!token) return t('get_started');
    if (currentSubscription?.subscription?.tier === tier) return t('current_plan_button');
    if (tier === 'free') return t('downgrade');
    
    const tierOrder = ['free', 'starter', 'pro', 'ministry', 'enterprise'];
    const currentIndex = tierOrder.indexOf(currentSubscription?.subscription?.tier || 'free');
    const targetIndex = tierOrder.indexOf(tier);
    
    return targetIndex > currentIndex ? t('upgrade') : t('change_plan');
  };

  const isCurrentPlan = (tier) => currentSubscription?.subscription?.tier === tier;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => {
            // Navigate based on auth status
            if (!user) {
              navigate('/');
            } else if (window.history.length > 2 && document.referrer) {
              navigate(-1);
            } else {
              navigate('/dashboard');
            }
          }}
          className="mb-6"
          data-testid="pricing-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('back')}
        </Button>

        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('pricing_title')}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t('pricing_subtitle')}
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <span className={`text-sm ${!isAnnual ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500'}`}>
              {t('billing_monthly')}
            </span>
            <Switch
              checked={isAnnual}
              onCheckedChange={setIsAnnual}
            />
            <span className={`text-sm ${isAnnual ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500'}`}>
              {t('billing_annual')}
              <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700">
                {t('save_percent')}
              </Badge>
            </span>
          </div>
        </div>

        {/* Current Plan Banner */}
        {currentSubscription && (
          <div className="mb-8 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 dark:text-amber-400">{t('current_plan')}</p>
                <p className="text-lg font-semibold text-amber-900 dark:text-amber-100">
                  {currentSubscription.subscription.tier_name}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('ai_generations')}: {currentSubscription.usage.ai_generations_used} / {currentSubscription.usage.ai_generations_limit === -1 ? '∞' : currentSubscription.usage.ai_generations_limit}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('projects')}: {currentSubscription.usage.projects_count} / {currentSubscription.usage.projects_limit === -1 ? '∞' : currentSubscription.usage.projects_limit}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.filter(t => t.tier !== 'enterprise').map((tier) => {
            const tierConfig = TIER_FEATURES[tier.tier] || {};
            const Icon = tierConfig.icon || Zap;
            const price = isAnnual ? tier.price_annual : tier.price_monthly;
            const monthlyEquivalent = isAnnual ? (tier.price_annual / 12).toFixed(0) : tier.price_monthly;

            return (
              <Card
                key={tier.tier}
                data-testid={`pricing-tier-${tier.tier}`}
                className={`relative ${tierConfig.color} border-2 ${
                  tierConfig.popular ? 'border-amber-500 shadow-lg scale-105' : 'border-gray-200 dark:border-gray-700'
                } ${isCurrentPlan(tier.tier) ? 'ring-2 ring-green-500' : ''}`}
              >
                {tierConfig.badge && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white">
                    {tierConfig.badge === 'Most Popular' ? t('most_popular') : t('best_value')}
                  </Badge>
                )}
                
                {isCurrentPlan(tier.tier) && (
                  <Badge className="absolute -top-3 right-4 bg-green-500 text-white">
                    {t('current_plan')}
                  </Badge>
                )}

                <CardHeader className="text-center pb-2">
                  <div className="mx-auto mb-2 p-3 rounded-full bg-white dark:bg-gray-800 shadow-sm w-fit">
                    <Icon className="w-6 h-6 text-amber-500" />
                  </div>
                  <CardTitle className="text-xl">{tier.name}</CardTitle>
                  <CardDescription>
                    {tier.tier === 'free' && t('plan_free_desc')}
                    {tier.tier === 'starter' && t('plan_starter_desc')}
                    {tier.tier === 'pro' && t('plan_pro_desc')}
                    {tier.tier === 'ministry' && t('plan_ministry_desc')}
                  </CardDescription>
                </CardHeader>

                <CardContent className="text-center">
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      ${monthlyEquivalent}
                    </span>
                    <span className="text-gray-500">{t('per_month')}</span>
                    {isAnnual && tier.price_annual > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        ${tier.price_annual}/year {t('billed_annually')}
                      </p>
                    )}
                  </div>

                  <ul className="space-y-3 text-left text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>
                        {tier.features.max_projects === -1 ? t('unlimited') : tier.features.max_projects} {t('projects_feature')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>
                        {tier.features.ai_generations_per_month === -1 ? t('unlimited') : tier.features.ai_generations_per_month} {t('ai_generations_feature')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {tier.features.export_pptx ? (
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      )}
                      <span className={!tier.features.export_pptx ? 'text-gray-400' : ''}>
                        {t('pptx_export')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {!tier.features.watermark ? (
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      )}
                      <span className={tier.features.watermark ? 'text-gray-400' : ''}>
                        {t('watermark_free')}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {tier.features.can_create_community ? (
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      )}
                      <span className={!tier.features.can_create_community ? 'text-gray-400' : ''}>
                        {tier.features.can_create_community 
                          ? `${tier.features.max_community_members === -1 ? t('unlimited') : tier.features.max_community_members} ${t('members_per_community')}`
                          : t('create_communities')
                        }
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {tier.features.service_calendar ? (
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-gray-300 flex-shrink-0" />
                      )}
                      <span className={!tier.features.service_calendar ? 'text-gray-400' : ''}>
                        {t('service_calendar')}
                      </span>
                    </li>
                  </ul>
                </CardContent>

                <CardFooter>
                  <Button
                    className={`w-full ${
                      tierConfig.popular
                        ? 'bg-amber-500 hover:bg-amber-600 text-white'
                        : isCurrentPlan(tier.tier)
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : ''
                    }`}
                    variant={tierConfig.popular || isCurrentPlan(tier.tier) ? 'default' : 'outline'}
                    onClick={() => handleUpgrade(tier.tier)}
                    disabled={upgrading === tier.tier || isCurrentPlan(tier.tier)}
                  >
                    {upgrading === tier.tier ? t('processing') : getButtonText(tier.tier)}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Lumera + SlideCon Combo Callout — reciprocal of the Lumera side */}
        <div
          className="mt-12 rounded-2xl overflow-hidden relative border border-teal-500/30"
          data-testid="lumera-combo-callout"
          style={{
            background:
              "linear-gradient(135deg, rgba(20, 184, 166, 0.10) 0%, rgba(15, 23, 42, 0.4) 100%)",
          }}
        >
          <div className="p-8 sm:p-10 text-center">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-teal-500/20 border border-teal-400/40 text-teal-300 text-[11px] font-semibold uppercase tracking-wider">
                {t('lumera_combo_badge') || 'Now Included'}
              </span>
              <span className="text-sm text-slate-400">
                {t('lumera_combo_in_every') || 'In every paid plan'}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
              {t('lumera_combo_title') || 'Lumera + SlideCon, one subscription'}
            </h2>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              {t('lumera_combo_desc') || 'Bible study in Lumera + professional slide decks in SlideCon. No extra fee — any Premium plan now includes Lumera Pro.'}
            </p>
            <ul className="grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto mb-6 text-left">
              {[
                t('lumera_combo_bullet_1') || 'Lumera Pro account linked automatically',
                t('lumera_combo_bullet_2') || 'Any conversation → slides in one tap',
                t('lumera_combo_bullet_3') || 'Sunday-ready presentation templates',
                t('lumera_combo_bullet_4') || 'No separate billing, no redemption code',
              ].map((bullet, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
                  <Check className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
            <Button
              onClick={() => document.querySelector('[data-testid^="pricing-tier"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white gap-2"
              data-testid="lumera-combo-choose-plan-btn"
            >
              <Sparkles className="w-4 h-4" />
              {t('lumera_combo_cta') || 'Choose a plan'}
            </Button>
            <p className="mt-4 text-[11px] text-slate-500">
              {t('lumera_combo_footnote') || 'Free tier gets a 7-day Lumera Pro trial · switch or cancel any time.'}
            </p>
          </div>
        </div>

        {/* Enterprise CTA */}
        <div className="mt-12 text-center p-8 bg-gray-100 dark:bg-gray-800 rounded-2xl">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('enterprise_title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {t('enterprise_desc')}
          </p>
          <Button variant="outline" size="lg">
            {t('contact_sales')}
          </Button>
        </div>

        {/* AI Credit Packages */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('ai_credits_title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-300">
              {t('ai_credits_desc')}
            </p>
            {creditBalance && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                <CreditCard className="w-4 h-4 text-amber-600" />
                <span className="text-amber-700 dark:text-amber-300 font-medium">
                  {t('your_balance')}: {creditBalance.remaining === -1 ? t('unlimited') : `${creditBalance.remaining} ${t('credits')}`}
                  {creditBalance.bonus_credits > 0 && (
                    <span className="text-amber-500 ml-1">(+{creditBalance.bonus_credits} {t('bonus_credits')})</span>
                  )}
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {creditPackages.map((pkg) => (
              <Card
                key={pkg.id}
                className={`relative ${pkg.popular ? 'border-2 border-amber-500 shadow-lg' : 'border border-gray-200 dark:border-gray-700'}`}
              >
                {pkg.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white">
                    <Star className="w-3 h-3 mr-1" /> {t('most_popular')}
                  </Badge>
                )}
                
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-lg">{pkg.name}</CardTitle>
                  <CardDescription>{pkg.description}</CardDescription>
                </CardHeader>

                <CardContent className="text-center">
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">
                      ${pkg.price}
                    </span>
                    {pkg.savings && (
                      <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700">
                        {pkg.savings}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    ${(pkg.price / pkg.credits * 100).toFixed(1)}¢ {t('per_credit')}
                  </p>
                </CardContent>

                <CardFooter>
                  <Button
                    className={`w-full ${pkg.popular ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
                    variant={pkg.popular ? 'default' : 'outline'}
                    onClick={() => handlePurchaseCredits(pkg.id)}
                    disabled={purchasingCredits === pkg.id}
                  >
                    {purchasingCredits === pkg.id ? t('processing') : t('buy_credits')}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-900 dark:text-white">
            {t('faq_title')}
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('faq_change_plans')}</h3>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                {t('faq_change_plans_answer')}
              </p>
            </div>
            <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('faq_data_downgrade')}</h3>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                {t('faq_data_downgrade_answer')}
              </p>
            </div>
            <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('faq_refunds')}</h3>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                {t('faq_refunds_answer')}
              </p>
            </div>
            <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 dark:text-white">{t('faq_church_discount')}</h3>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                {t('faq_church_discount_answer')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment provider chooser dialog */}
      <Dialog open={!!payTier} onOpenChange={(o) => { if (!o) setPayTier(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="pay-chooser-dialog">
          <DialogHeader>
            <DialogTitle>Choose how to pay</DialogTitle>
            <DialogDescription>
              {payTier && `SlideCon ${payTier.charAt(0).toUpperCase() + payTier.slice(1)} — 30 days`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Button
              onClick={payWithStripe}
              disabled={!!upgrading}
              data-testid="pay-with-stripe-btn"
              className="w-full justify-between h-14 bg-[#635BFF] hover:bg-[#4d43ff] text-white"
            >
              <span className="flex items-center gap-3">
                <CreditCard className="w-5 h-5" />
                <span className="text-left">
                  <span className="block font-semibold">Card via Stripe</span>
                  <span className="block text-[11px] opacity-80">Apple Pay · Google Pay · card</span>
                </span>
              </span>
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              onClick={payWithPayPal}
              disabled={!!upgrading}
              data-testid="pay-with-paypal-btn"
              className="w-full justify-between h-14 bg-[#003087] hover:bg-[#002264] text-white"
            >
              <span className="flex items-center gap-3">
                <span className="font-bold text-lg italic">PayPal</span>
                <span className="text-[11px] opacity-80">PayPal balance · card via PayPal</span>
              </span>
              <ArrowRight className="w-4 h-4" />
            </Button>
            <p className="text-[11px] text-slate-500 text-center pt-2">
              Payments are processed by Stripe or PayPal. You&apos;ll be redirected to their secure checkout.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage subscription strip — floats bottom-right on desktop, docks at bottom on mobile */}
      {currentSubscription?.subscription?.tier && currentSubscription.subscription.tier !== 'free' && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-40 bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700 p-3 sm:max-w-xs mx-auto sm:mx-0">
          <p className="text-xs text-slate-500 mb-1">Current: {currentSubscription.subscription.tier}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCancelDialogOpen(true)}
              className="text-xs text-red-600 hover:underline"
              data-testid="cancel-sub-btn"
              disabled={canceling}
            >
              Cancel subscription
            </button>
            <span className="text-slate-300">·</span>
            <button
              type="button"
              onClick={() => navigate('/invoices')}
              className="text-xs text-slate-600 dark:text-slate-300 hover:underline"
              data-testid="invoices-btn"
            >
              Invoices
            </button>
          </div>
        </div>
      )}

      {/* Cancel Subscription confirmation */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent data-testid="cancel-subscription-alert">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              You&apos;ll keep your current tier until the end of the billing period, then downgrade to Free. You can re-subscribe any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-subscription-abort" disabled={canceling}>
              Keep subscription
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              disabled={canceling}
              data-testid="cancel-subscription-confirm"
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {canceling ? 'Canceling…' : 'Yes, cancel'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
